import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, Roles, ThemeProvider, ToastProvider, useAuth, type Role } from './providers';
import { Layout } from './Layout';
import { Toaster } from '../components/Feedback';
import { AppLoader } from '../components/PageHeader';
import { LoginPage } from '../pages/LoginPage';
import { NotFoundPage } from '../pages/NotFoundPage';

import { FeesListPage } from '../pages/fees/FeesListPage';
import { FeesSubmissionPage } from '../pages/fees/FeesSubmissionPage';
import {
  AdvancedSearchPage, ChangeStudentFeesPage, FeeDetailsPage,
  FeesSlipPage, FeesSlipsPage, GenerateAdvancePage,
} from '../pages/fees/FeeScreens';

import { CoursesPage, StaffPage, StudentsPage, TeachersPage } from '../pages/records/RecordPages';
import {
  CourseFormPage, StaffFormPage, StudentFormPage, TeacherFormPage, TestFormPage,
} from '../pages/records/EntityForms';
import { CourseSchedulesPage, EnrollmentPage } from '../pages/records/EnrollmentAndSchedules';

import { ExpensesPage, StaffSalaryPage, TeacherSalaryPage } from '../pages/finance/FinancePages';
import {
  StaffSalaryPaymentPage, TeacherMonthlyPaymentsPage, TeacherSalaryPaymentPage, TeacherStatementPage,
} from '../pages/finance/SalaryScreens';

import { MasterInformationPage, TestsPage, UsersPage } from '../pages/admin/AdminPages';
import {
  CourseStudentsPage, ExpenseSummaryPage,
  StudentTestReportPage, UserEditPage, UserSessionsPage,
} from '../pages/admin/AdminScreens';
import { DashboardPage } from '../pages/admin/DashboardPage';

import {
  MyCoursesPage, MyEnrolledCoursesPage, MyFeesPage, MyInfoPage, MyTestScoresPage,
} from '../pages/selfservice/SelfServicePages';

// Order matters: tokens define the variables, base resets and sets type, components style
// the pieces, shell places them. Each later file may use anything the earlier ones defined.
import '@fontsource-variable/inter';
import '../styles/tokens.css';
import '../styles/base.css';
import '../styles/components.css';
import '../styles/shell.css';

/**
 * Client-side route guarding is a convenience, not a security boundary. Every endpoint
 * carries its own [Authorize(Roles = ...)] on the server, copied action-for-action from the
 * legacy controllers. This only decides what the user is shown.
 */
function RequireAuth({ roles, children }: { roles?: Role[]; children: ReactNode }) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="center-screen">
        <AppLoader />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && !hasRole(...roles)) {
    return (
      <div className="card">
        <h2 className="card__title">Not available</h2>
        <p>Your role does not have access to this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}

function Guarded({ roles, children }: { roles: Role[]; children: ReactNode }) {
  return <RequireAuth roles={roles}>{children}</RequireAuth>;
}

const ADMIN = [Roles.Director, Roles.Coordinator];
const MONEY = [Roles.Director, Roles.Cashier];
const DIRECTOR = [Roles.Director];

export function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route index element={<DashboardPage />} />

                {/* ---------- Fees ---------- */}
                <Route path="fees" element={<Guarded roles={MONEY}><FeesListPage /></Guarded>} />
                <Route path="fees/submission" element={<Guarded roles={MONEY}><FeesSubmissionPage /></Guarded>} />
                <Route path="fees/slips" element={<Guarded roles={MONEY}><FeesSlipsPage /></Guarded>} />
                <Route path="fees/slip/:id" element={<Guarded roles={MONEY}><FeesSlipPage /></Guarded>} />
                <Route path="fees/details/:feesId" element={<Guarded roles={MONEY}><FeeDetailsPage /></Guarded>} />
                <Route path="fees/search" element={<Guarded roles={MONEY}><AdvancedSearchPage /></Guarded>} />
                <Route path="fees/advance" element={<Guarded roles={MONEY}><GenerateAdvancePage /></Guarded>} />
                <Route path="fees/change/:studentId" element={<Guarded roles={ADMIN}><ChangeStudentFeesPage /></Guarded>} />

                {/* ---------- Students ---------- */}
                <Route path="students" element={<Guarded roles={ADMIN}><StudentsPage /></Guarded>} />
                <Route path="students/new" element={<Guarded roles={ADMIN}><StudentFormPage /></Guarded>} />
                <Route path="students/:id/edit" element={<Guarded roles={ADMIN}><StudentFormPage /></Guarded>} />
                <Route path="students/:studentId/enroll" element={<Guarded roles={ADMIN}><EnrollmentPage /></Guarded>} />
                <Route path="students/:studentId/tests" element={<Guarded roles={ADMIN}><StudentTestReportPage /></Guarded>} />

                {/* ---------- Teachers ---------- */}
                <Route path="teachers" element={<Guarded roles={ADMIN}><TeachersPage /></Guarded>} />
                <Route path="teachers/new" element={<Guarded roles={ADMIN}><TeacherFormPage /></Guarded>} />
                <Route path="teachers/:id/edit" element={<Guarded roles={ADMIN}><TeacherFormPage /></Guarded>} />

                {/* ---------- Staff ---------- */}
                <Route path="staff" element={<Guarded roles={ADMIN}><StaffPage /></Guarded>} />
                <Route path="staff/new" element={<Guarded roles={ADMIN}><StaffFormPage /></Guarded>} />
                <Route path="staff/:id/edit" element={<Guarded roles={ADMIN}><StaffFormPage /></Guarded>} />

                {/* ---------- Courses ---------- */}
                <Route path="courses" element={<Guarded roles={ADMIN}><CoursesPage /></Guarded>} />
                <Route path="courses/new" element={<Guarded roles={ADMIN}><CourseFormPage /></Guarded>} />
                <Route path="courses/:id/edit" element={<Guarded roles={ADMIN}><CourseFormPage /></Guarded>} />
                <Route path="courses/:courseId/schedules" element={<Guarded roles={ADMIN}><CourseSchedulesPage /></Guarded>} />
                <Route path="courses/:courseId/students" element={<Guarded roles={ADMIN}><CourseStudentsPage /></Guarded>} />

                {/* ---------- Tests ---------- */}
                <Route path="tests" element={<Guarded roles={ADMIN}><TestsPage /></Guarded>} />
                <Route path="tests/new" element={<Guarded roles={ADMIN}><TestFormPage /></Guarded>} />
                <Route path="tests/:id/edit" element={<Guarded roles={ADMIN}><TestFormPage /></Guarded>} />

                {/* ---------- Finance ---------- */}
                <Route path="salary/teachers" element={<Guarded roles={DIRECTOR}><TeacherSalaryPage /></Guarded>} />
                <Route path="salary/teachers/:id/pay" element={<Guarded roles={DIRECTOR}><TeacherSalaryPaymentPage /></Guarded>} />
                <Route path="salary/teachers/:id/statement" element={<Guarded roles={DIRECTOR}><TeacherStatementPage /></Guarded>} />
                <Route path="salary/teachers/:id/payments" element={<Guarded roles={DIRECTOR}><TeacherMonthlyPaymentsPage /></Guarded>} />
                <Route path="salary/staff" element={<Guarded roles={DIRECTOR}><StaffSalaryPage /></Guarded>} />
                <Route path="salary/staff/:id/pay" element={<Guarded roles={DIRECTOR}><StaffSalaryPaymentPage /></Guarded>} />
                <Route path="expenses" element={<Guarded roles={MONEY}><ExpensesPage /></Guarded>} />
                <Route path="expenses/summary" element={<Guarded roles={DIRECTOR}><ExpenseSummaryPage /></Guarded>} />

                {/* ---------- Administration ---------- */}
                <Route path="users" element={<Guarded roles={ADMIN}><UsersPage /></Guarded>} />
                <Route path="users/:userId/edit" element={<Guarded roles={ADMIN}><UserEditPage /></Guarded>} />
                <Route path="users/:userId/sessions" element={<Guarded roles={ADMIN}><UserSessionsPage /></Guarded>} />
                <Route path="master-information" element={<Guarded roles={DIRECTOR}><MasterInformationPage /></Guarded>} />

                {/* ---------- Self service ----------
                    Unreachable today: Teacher and Student accounts are refused a token.
                    Kept so the structure stays uniform and re-enabling is server-side only.
                    See KnowledgeBase/findings/disabled-features.md. */}
                <Route path="me/info" element={<Guarded roles={[Roles.Student]}><MyInfoPage /></Guarded>} />
                <Route path="me/courses" element={<Guarded roles={[Roles.Student]}><MyEnrolledCoursesPage /></Guarded>} />
                <Route path="me/fees" element={<Guarded roles={[Roles.Student]}><MyFeesPage /></Guarded>} />
                <Route path="me/scores" element={<Guarded roles={[Roles.Student]}><MyTestScoresPage /></Guarded>} />
                <Route path="me/teaching" element={<Guarded roles={[Roles.Teacher]}><MyCoursesPage /></Guarded>} />

                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>

          <Toaster />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
